import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const btn = (bg, fg, br) => ({
  background: bg, color: fg, border: br ? `1px solid ${br}` : "none",
  padding: "0.5rem 1rem", borderRadius: 5,
  fontSize: "0.8rem", fontWeight: 800, cursor: "pointer",
});

const overlay = {
  position: "fixed", inset: 0, background: "rgba(4,10,26,0.88)",
  zIndex: 5000, display: "flex", alignItems: "flex-start",
  justifyContent: "center", padding: "3rem 1rem", overflowY: "auto",
};

const shell = {
  width: "min(880px, 100%)", background: "#0B1A2F",
  border: "1px solid rgba(184,145,90,0.4)", borderRadius: 10,
  padding: "1.6rem 1.8rem", color: "#F5EFE1",
  fontFamily: "'Inter', system-ui, sans-serif",
};


const LiveStatusHealthModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("health");
  const [health, setHealth] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [sam, setSam] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [h, c, s, p] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/live-status-health`, { credentials: "include" }).then(r => r.json()).catch(() => null),
        fetch(`${BACKEND_URL}/api/admin/runtime-config`,      { credentials: "include" }).then(r => r.json()).catch(() => null),
        fetch(`${BACKEND_URL}/api/admin/sam-gov-checklist`,   { credentials: "include" }).then(r => r.json()).catch(() => null),
        fetch(`${BACKEND_URL}/api/admin/morning-dossier/pending`, { credentials: "include" }).then(r => r.json()).catch(() => null),
      ]);
      setHealth(h); setCfg(c); setSam(s); setPending(p);
    } catch (e) { setErr("Load failed."); }
    setBusy(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const flipSandbox = async (mode) => {
    if (!window.confirm(mode === false
      ? "GO LIVE?\n\nThis flips vendor invites + CEO pings from preview to real Twilio/Resend/Stripe delivery immediately."
      : "Return to SANDBOX?\n\nAll outbound comms will stage but not send.")) return;
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/admin/runtime-config`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sandbox_mode_override: mode }),
    });
    const d = await r.json();
    setCfg({ ok: true, config: d.config });
    window.dispatchEvent(new Event("mf-sandbox-changed"));
    setBusy(false);
    await load();
  };

  const clearOverride = async () => {
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/admin/runtime-config`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sandbox_mode_override: "clear" }),
    });
    const d = await r.json();
    setCfg({ ok: true, config: d.config });
    window.dispatchEvent(new Event("mf-sandbox-changed"));
    setBusy(false);
  };

  const updateCaps = async (perHour, perDay) => {
    setBusy(true);
    const body = {};
    if (perHour !== undefined) body.outreach_cap_per_hour = perHour;
    if (perDay  !== undefined) body.outreach_cap_per_day  = perDay;
    await fetch(`${BACKEND_URL}/api/admin/runtime-config`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await load();
  };

  const releaseDossier = async () => {
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/admin/morning-dossier/release-now`, {
      method: "POST", credentials: "include",
    });
    const d = await r.json();
    alert(d.sandbox_mode
      ? `Dossier composed (sandbox mode) — ${d.queued_count || 0} events staged.\n\nPreview:\n${d.preview_body || ""}`
      : `Dossier sent — released_count=${d.released_count}`);
    await load();
  };

  const exportHandoff = () => {
    // Opens in new tab; browser downloads the .md file
    window.open(`${BACKEND_URL}/api/admin/handoff-dossier/export`, "_blank");
  };

  const toggleSamItem = async (key, done) => {
    const body = {};
    body[key] = !done;
    await fetch(`${BACKEND_URL}/api/admin/sam-gov-checklist`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await load();
  };

  const setCage = async () => {
    const cc = window.prompt("Enter your SAM.gov CAGE Code (5-char alphanumeric):");
    if (!cc) return;
    await fetch(`${BACKEND_URL}/api/admin/sam-gov-checklist`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cage_code: cc.trim().toUpperCase() }),
    });
    await load();
  };

  if (!open) return null;

  const config = cfg?.config || {};
  const isLive = config.live_status === "live";
  const isOverride = config.sandbox_source === "runtime_override";

  return (
    <div style={overlay} data-testid="live-status-modal" onClick={onClose}>
      <div style={shell} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase" }}>
              🛡 Institutional Readiness · R12
            </div>
            <div style={{ color: "#F5EFE1", fontSize: "1.2rem", fontWeight: 800, marginTop: 2 }}>
              Live Status Command Deck
            </div>
          </div>
          <button data-testid="lsh-close" onClick={onClose} style={btn("transparent", "#ADBED1", "#334155")}>✕ Close</button>
        </div>

        {/* Export Handoff Dossier - always available */}
        <div style={{ marginBottom: "0.9rem", display: "flex", justifyContent: "flex-end" }}>
          <button data-testid="lsh-export-handoff" onClick={exportHandoff}
                  style={btn("transparent", "#B8915A", "#B8915A")}>
            📦 Export Handoff Bundle (.md)
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem", borderBottom: "1px solid rgba(184,145,90,0.25)" }}>
          {[
            ["health",  "🩺 Readiness"],
            ["config",  "⚙️ Runtime Config"],
            ["dossier", "🌙 Dossier Queue"],
            ["samgov",  "📋 SAM.gov"],
          ].map(([k, l]) => (
            <button key={k} data-testid={`lsh-tab-${k}`} onClick={() => setTab(k)}
                    style={{
                      background: tab === k ? "rgba(184,145,90,0.15)" : "transparent",
                      color: tab === k ? "#F5EFE1" : "#ADBED1",
                      borderBottom: tab === k ? "2px solid #B8915A" : "2px solid transparent",
                      padding: "0.5rem 0.85rem", fontSize: "0.75rem", fontWeight: 700,
                      cursor: "pointer", border: "none",
                    }}>
              {l}
            </button>
          ))}
        </div>

        {busy && <div style={{ color: "#B8915A", fontSize: "0.75rem", marginBottom: "0.5rem" }}>Working…</div>}
        {err && <div style={{ color: "#FCA5A5", fontSize: "0.8rem", marginBottom: "0.5rem" }}>⚠ {err}</div>}

        {/* Readiness tab */}
        {tab === "health" && health && (
          <div data-testid="lsh-health-panel">
            <div style={{
              background: health.verdict === "READY_FOR_LIVE"
                ? "linear-gradient(90deg, rgba(0,220,120,0.18), rgba(90,200,216,0.14))"
                : "linear-gradient(90deg, rgba(220,38,38,0.18), rgba(184,145,90,0.14))",
              border: `1px solid ${health.verdict === "READY_FOR_LIVE" ? "rgba(0,220,120,0.5)" : "rgba(220,38,38,0.5)"}`,
              borderRadius: 8, padding: "0.9rem 1.1rem", marginBottom: "1rem",
            }}>
              <div style={{ color: "#F5EFE1", fontSize: "1.1rem", fontWeight: 800 }}>
                {health.verdict === "READY_FOR_LIVE" ? "🚀 READY FOR LIVE" : "⚠ BLOCKED"}
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 3 }}>
                {health.pass_count} pass · {health.warn_count} warn · {health.fail_count} fail
              </div>
            </div>
            {(health.checks || []).map(c => (
              <div key={c.component} data-testid={`lsh-check-${c.component}`}
                   style={{ display: "flex", justifyContent: "space-between",
                            padding: "0.55rem 0.7rem", borderBottom: "1px solid rgba(184,145,90,0.12)",
                            fontSize: "0.82rem" }}>
                <div>
                  <span style={{ color: c.status === "pass" ? "#00DC78" : c.status === "warn" ? "#FFB347" : "#FCA5A5", fontWeight: 800, marginRight: 8 }}>
                    {c.status === "pass" ? "✓" : c.status === "warn" ? "▲" : "✗"}
                  </span>
                  <span style={{ color: "#F5EFE1" }}>{c.component}</span>
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.75rem", textAlign: "right", maxWidth: 380 }}>
                  {c.detail}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Runtime Config tab */}
        {tab === "config" && (
          <div data-testid="lsh-config-panel">
            <div style={{
              background: isLive ? "rgba(0,220,120,0.14)" : "rgba(184,145,90,0.12)",
              border: `1px solid ${isLive ? "rgba(0,220,120,0.5)" : "rgba(184,145,90,0.35)"}`,
              borderRadius: 8, padding: "0.9rem 1.1rem", marginBottom: "1rem",
              display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem",
            }}>
              <div>
                <div style={{ color: "#F5EFE1", fontSize: "1rem", fontWeight: 800 }}>
                  {isLive ? "🟢 LIVE" : "🟡 SANDBOX"}
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginTop: 2 }}>
                  Source: <strong>{config.sandbox_source}</strong>
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                <button data-testid="lsh-go-live" onClick={() => flipSandbox(false)}
                        style={btn("linear-gradient(90deg,#00DC78,#5AC8D8)", "#062617")}>🚀 Go Live</button>
                <button data-testid="lsh-go-sandbox" onClick={() => flipSandbox(true)}
                        style={btn("transparent", "#B8915A", "#B8915A")}>🛡 Sandbox</button>
                {isOverride && (
                  <button data-testid="lsh-clear-override" onClick={clearOverride}
                          style={btn("transparent", "#ADBED1", "#334155")}>↺ Clear Override</button>
                )}
              </div>
            </div>

            <div style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(184,145,90,0.2)", borderRadius: 6, padding: "0.85rem 1rem" }}>
              <div style={{ color: "#B8915A", fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>
                Outreach Caps · Rolling Window
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
                <div>
                  <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Per hour</div>
                  <input data-testid="lsh-cap-hour" type="number" min={1} defaultValue={config.outreach_cap_per_hour}
                         onBlur={e => updateCaps(parseInt(e.target.value) || 1, undefined)}
                         style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                  border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5,
                                  padding: "0.5rem", fontFamily: "inherit" }} />
                </div>
                <div>
                  <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginBottom: 4 }}>Per day</div>
                  <input data-testid="lsh-cap-day" type="number" min={1} defaultValue={config.outreach_cap_per_day}
                         onBlur={e => updateCaps(undefined, parseInt(e.target.value) || 1)}
                         style={{ width: "100%", background: "rgba(2,10,31,0.6)", color: "#fff",
                                  border: "1px solid rgba(184,145,90,0.35)", borderRadius: 5,
                                  padding: "0.5rem", fontFamily: "inherit" }} />
                </div>
              </div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 10 }}>
                Current usage · last hour: <strong>{cfg?.usage?.last_hour ?? 0}</strong> · last 24h: <strong>{cfg?.usage?.last_24h ?? 0}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Dossier tab */}
        {tab === "dossier" && (
          <div data-testid="lsh-dossier-panel">
            <div style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(184,145,90,0.2)", borderRadius: 6, padding: "0.85rem 1rem", marginBottom: "0.9rem" }}>
              <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
                🌙 Quiet-Hours Rule · 10 PM - 6 AM CT
              </div>
              <div style={{ color: "#F5EFE1", fontSize: "0.83rem" }}>
                Non-urgent CEO pings deferred to the morning dossier — released as one digest SMS at 6:00 AM Central every day.
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.7rem" }}>
              <div style={{ color: "#F5EFE1", fontSize: "0.9rem", fontWeight: 700 }}>
                Pending: <strong style={{ color: "#B8915A" }}>{pending?.count ?? 0}</strong>
              </div>
              <button data-testid="lsh-release-dossier" onClick={releaseDossier}
                      disabled={!pending?.count} style={{ ...btn("linear-gradient(90deg,#B8915A,#5AC8D8)", "#0a1929"), opacity: pending?.count ? 1 : 0.5 }}>
                📡 Release Now
              </button>
            </div>

            {(pending?.queued || []).map((q, i) => (
              <div key={i} data-testid={`lsh-dossier-row-${i}`}
                   style={{ padding: "0.5rem 0.7rem", borderBottom: "1px solid rgba(184,145,90,0.12)", fontSize: "0.78rem" }}>
                <div style={{ color: "#F5EFE1" }}>{q.summary}</div>
                <div style={{ color: "#7E92A8", fontSize: "0.68rem", marginTop: 2 }}>
                  {q.event} · queued {new Date(q.queued_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SAM.gov tab */}
        {tab === "samgov" && sam && (
          <div data-testid="lsh-samgov-panel">
            <div style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(184,145,90,0.2)", borderRadius: 6, padding: "0.85rem 1rem", marginBottom: "0.9rem" }}>
              <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 3 }}>
                📋 Federal Registration · Entity: {sam.entity_name}
              </div>
              <div style={{ color: "#F5EFE1", fontSize: "0.85rem" }}>Progress: {sam.progress}</div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 4 }}>
                NAICS: {(sam.naics_targets || []).join(" · ")}
              </div>
            </div>

            {(sam.items || []).map(item => (
              <div key={item.id} data-testid={`lsh-sam-${item.id}`}
                   style={{
                     padding: "0.7rem 0.9rem", marginBottom: "0.5rem",
                     background: item.done ? "rgba(0,220,120,0.08)" : "rgba(0,0,0,0.2)",
                     border: `1px solid ${item.done ? "rgba(0,220,120,0.3)" : "rgba(184,145,90,0.2)"}`,
                     borderRadius: 5,
                   }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.7rem" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: "#F5EFE1", fontSize: "0.86rem", fontWeight: 700 }}>
                      {item.done ? "✓" : "○"} {item.label}
                    </div>
                    <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginTop: 3 }}>{item.note}</div>
                    {item.cage_code && (
                      <div style={{ color: "#00DC78", fontSize: "0.75rem", marginTop: 4, fontFamily: "monospace" }}>
                        CAGE: {item.cage_code}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", alignItems: "flex-end" }}>
                    {item.id === "cage_code_capture" ? (
                      <button data-testid={`lsh-sam-cage-set`} onClick={setCage} style={btn("#B8915A", "#0a1929")}>
                        {item.done ? "Update" : "Enter Code"}
                      </button>
                    ) : (
                      <button data-testid={`lsh-sam-toggle-${item.id}`}
                              onClick={() => toggleSamItem(item.id === "assertions" ? "assertions_done" : "reps_and_certs_done", item.done)}
                              style={btn(item.done ? "transparent" : "#B8915A", item.done ? "#ADBED1" : "#0a1929", item.done ? "#334155" : undefined)}>
                        {item.done ? "Undo" : "Mark Done"}
                      </button>
                    )}
                    <a data-testid={`lsh-sam-open-${item.id}`} href={item.url} target="_blank" rel="noopener noreferrer"
                       style={{ color: "#5AC8D8", fontSize: "0.7rem", textDecoration: "none" }}>Open ↗</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default LiveStatusHealthModal;
